import { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Link2, Copy, Unplug, CheckCircle2, XCircle, Pencil, RotateCcw, Save, Upload, Users, X } from 'lucide-react';
import { canEdit as canEditSection } from '@/lib/auth';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { ADMIN_MENTORS } from '@/constants/testIds';

function EditMentorDialog({ mentor, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: mentor.name || '',
        email: mentor.email || '',
        phone: mentor.phone || '',
        domain: mentor.domain || '',
        headline: mentor.headline || '',
        bio: mentor.bio || '',
        tagline: mentor.tagline || '',
        avatar_url: mentor.avatar_url || '',
        expertise: (mentor.expertise || []).join(', '),
        languages: (mentor.languages || []).join(', '),
        years_experience: mentor.years_experience ?? '',
        rating: mentor.rating ?? '',
        sessions_completed: mentor.sessions_completed ?? '',
    });
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [uploading, setUploading] = useState(false);

    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';  // allow re-selecting the same file
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error('Please choose an image file (PNG, JPG, or WEBP).');
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post(`/admin/mentors/${mentor.id}/avatar`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setForm((f) => ({ ...f, avatar_url: data.avatar_url }));
            toast.success('Photo uploaded');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Photo upload failed');
        } finally {
            setUploading(false);
        }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                name: form.name || null,
                email: form.email || null,
                phone: form.phone ? form.phone.trim() : null,
                domain: form.domain || null,
                headline: form.headline || null,
                bio: form.bio || null,
                tagline: form.tagline || null,
                avatar_url: form.avatar_url || null,
                expertise: form.expertise ? form.expertise.split(',').map((s) => s.trim()).filter(Boolean) : null,
                languages: form.languages ? form.languages.split(',').map((s) => s.trim()).filter(Boolean) : null,
                years_experience: form.years_experience === '' ? null : Number(form.years_experience),
                rating: form.rating === '' ? null : Number(form.rating),
                sessions_completed: form.sessions_completed === '' ? null : Number(form.sessions_completed),
            };
            await api.patch(`/admin/mentors/${mentor.id}`, payload);
            toast.success('Mentor card updated');
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const resetOverrides = async () => {
        if (!window.confirm('Reset all admin overrides? Fields will revert to what gradnext returns.')) return;
        setResetting(true);
        try {
            await api.delete(`/admin/mentors/${mentor.id}/overrides`);
            toast.success('Overrides cleared');
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed');
        } finally {
            setResetting(false);
        }
    };

    return (
        <Dialog open={!!mentor} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[90vh] overflow-auto sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display">Edit mentor card — {mentor.name}</DialogTitle>
                    <p className="text-sm text-muted-foreground">Your edits override what gradnext returns. Leave a field blank to inherit from gradnext.</p>
                </DialogHeader>
                <form onSubmit={save} className="space-y-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                            <Label>Photo</Label>
                            <div className="flex items-center gap-3">
                                <Avatar className="h-14 w-14 border">
                                    <AvatarImage src={form.avatar_url} />
                                    <AvatarFallback className="bg-secondary text-primary">?</AvatarFallback>
                                </Avatar>
                                <div className="flex-1 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            id="mentor-photo-file"
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handlePhotoUpload}
                                            data-testid="admin-mentor-photo-input"
                                        />
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            size="sm"
                                            disabled={uploading}
                                            onClick={() => document.getElementById('mentor-photo-file')?.click()}
                                            data-testid="admin-mentor-photo-upload"
                                        >
                                            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                                            Upload photo
                                        </Button>
                                        {form.avatar_url && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                                onClick={() => setForm((f) => ({ ...f, avatar_url: '' }))}
                                            >
                                                <XCircle className="mr-1 h-4 w-4" /> Remove
                                            </Button>
                                        )}
                                    </div>
                                    <Input
                                        value={form.avatar_url}
                                        onChange={(e) => setForm((f) => ({ ...f, avatar_url: e.target.value }))}
                                        placeholder="…or paste an image URL (https://…)"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Display name</Label>
                            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Mentor email <span className="text-xs text-muted-foreground">(for booking notifications)</span></Label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                placeholder="mentor@example.com"
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                                Used to send the mentor booking / cancel / reschedule notifications and attach them as an attendee on the calendar invite.
                            </p>
                        </div>
                        <div className="sm:col-span-2">
                            <Label>WhatsApp / Phone number <span className="text-xs text-muted-foreground">(for WhatsApp notifications)</span></Label>
                            <Input
                                type="tel"
                                value={form.phone}
                                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                                placeholder="+91 98765 43210"
                                data-testid="admin-mentor-phone-input"
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                                Include the country code (e.g. <span className="font-mono">+91…</span>). This will be used to send the mentor WhatsApp notifications for their sessions.
                            </p>
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Domain <span className="text-xs text-muted-foreground">(shown as a filter on the student panel)</span></Label>
                            <Input
                                value={form.domain}
                                onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                                placeholder="e.g. Consulting, Product, Finance, Tech"
                                data-testid="admin-mentor-domain-input"
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                                Broad category tag. Students can filter mentors by this domain. Reuse the same value across mentors so filters stay clean.
                            </p>
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Headline</Label>
                            <Input value={form.headline} onChange={(e) => setForm((f) => ({ ...f, headline: e.target.value }))} placeholder="e.g. Ex-BCG · Strategy Consulting" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Tagline (short pull-quote)</Label>
                            <Input value={form.tagline} onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))} placeholder="e.g. Structured thinking wins interviews." />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Bio</Label>
                            <Textarea rows={4} value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Expertise (comma separated)</Label>
                            <Input value={form.expertise} onChange={(e) => setForm((f) => ({ ...f, expertise: e.target.value }))} placeholder="Strategy Consulting, Case Prep, MBA Admissions" />
                        </div>
                        <div>
                            <Label>Languages (comma separated)</Label>
                            <Input value={form.languages} onChange={(e) => setForm((f) => ({ ...f, languages: e.target.value }))} />
                        </div>
                        <div>
                            <Label>Years of experience</Label>
                            <Input type="number" min="0" value={form.years_experience} onChange={(e) => setForm((f) => ({ ...f, years_experience: e.target.value }))} />
                        </div>
                        <div>
                            <Label>Rating (0–5)</Label>
                            <Input type="number" min="0" max="5" step="any" value={form.rating} onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))} />
                        </div>
                        <div>
                            <Label>Sessions completed</Label>
                            <Input type="number" min="0" value={form.sessions_completed} onChange={(e) => setForm((f) => ({ ...f, sessions_completed: e.target.value }))} />
                        </div>
                    </div>

                    <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
                        <Button type="button" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={resetOverrides} disabled={resetting}>
                            {resetting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                            Reset overrides
                        </Button>
                        <div className="flex flex-1 justify-end gap-2">
                            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                            <Button type="submit" disabled={saving}>
                                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                                Save changes
                            </Button>
                        </div>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function BulkEditMentorsDialog({ mentorIds, count, onClose, onSaved }) {
    const [form, setForm] = useState({
        domain: '',
        headline: '',
        tagline: '',
        expertise: '',
        languages: '',
        years_experience: '',
        rating: '',
        sessions_completed: '',
    });
    const [saving, setSaving] = useState(false);

    // Build a payload containing only the fields the admin actually filled in.
    const buildFields = () => {
        const f = {};
        if (form.domain.trim()) f.domain = form.domain.trim();
        if (form.headline.trim()) f.headline = form.headline.trim();
        if (form.tagline.trim()) f.tagline = form.tagline.trim();
        const exp = form.expertise.split(',').map((s) => s.trim()).filter(Boolean);
        if (exp.length) f.expertise = exp;
        const langs = form.languages.split(',').map((s) => s.trim()).filter(Boolean);
        if (langs.length) f.languages = langs;
        if (form.years_experience !== '') f.years_experience = Number(form.years_experience);
        if (form.rating !== '') f.rating = Number(form.rating);
        if (form.sessions_completed !== '') f.sessions_completed = Number(form.sessions_completed);
        return f;
    };

    const fieldsToApply = buildFields();
    const nothingToApply = Object.keys(fieldsToApply).length === 0;

    const save = async (e) => {
        e.preventDefault();
        if (nothingToApply) {
            toast.error('Fill at least one field to apply.');
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post('/admin/mentors/bulk-update', {
                mentor_ids: mentorIds,
                fields: fieldsToApply,
            });
            toast.success(`Updated ${data.updated} mentor${data.updated === 1 ? '' : 's'}`);
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Bulk update failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[90vh] overflow-auto sm:max-w-2xl" data-testid="admin-mentors-bulk-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Bulk edit — {count} mentor{count === 1 ? '' : 's'}</DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        Only the fields you fill in below will be applied to all selected mentors. Leave a field blank to leave it unchanged.
                    </p>
                </DialogHeader>
                <form onSubmit={save} className="space-y-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                            <Label>Domain <span className="text-xs text-muted-foreground">(student-panel filter)</span></Label>
                            <Input
                                value={form.domain}
                                onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                                placeholder="e.g. Consulting, Product, Finance"
                                data-testid="bulk-domain-input"
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Headline</Label>
                            <Input value={form.headline} onChange={(e) => setForm((f) => ({ ...f, headline: e.target.value }))} placeholder="e.g. Ex-BCG · Strategy Consulting" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Tagline</Label>
                            <Input value={form.tagline} onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))} placeholder="Short pull-quote" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Expertise (comma separated)</Label>
                            <Input value={form.expertise} onChange={(e) => setForm((f) => ({ ...f, expertise: e.target.value }))} placeholder="Strategy Consulting, Case Prep, MBA Admissions" />
                        </div>
                        <div>
                            <Label>Languages (comma separated)</Label>
                            <Input value={form.languages} onChange={(e) => setForm((f) => ({ ...f, languages: e.target.value }))} placeholder="English, Hindi" />
                        </div>
                        <div>
                            <Label>Years of experience</Label>
                            <Input type="number" min="0" value={form.years_experience} onChange={(e) => setForm((f) => ({ ...f, years_experience: e.target.value }))} />
                        </div>
                        <div>
                            <Label>Rating (0–5)</Label>
                            <Input type="number" min="0" max="5" step="any" value={form.rating} onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))} />
                        </div>
                        <div>
                            <Label>Sessions completed</Label>
                            <Input type="number" min="0" value={form.sessions_completed} onChange={(e) => setForm((f) => ({ ...f, sessions_completed: e.target.value }))} />
                        </div>
                    </div>

                    {!nothingToApply && (
                        <div className="rounded-lg border border-accent/30 bg-secondary p-3 text-xs text-primary">
                            Will apply <strong>{Object.keys(fieldsToApply).join(', ')}</strong> to <strong>{count}</strong> selected mentor{count === 1 ? '' : 's'}.
                        </div>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={saving || nothingToApply} data-testid="bulk-save-button">
                            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                            Apply to {count}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function AdminMentorsPage() {
    const [mentors, setMentors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [linkModal, setLinkModal] = useState(null);
    const [editing, setEditing] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkOpen, setBulkOpen] = useState(false);
    const canEdit = canEditSection('mentors');

    const toggleSelect = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };
    const clearSelection = () => setSelectedIds([]);
    const allSelected = mentors.length > 0 && selectedIds.length === mentors.length;
    const toggleSelectAll = () => setSelectedIds(allSelected ? [] : mentors.map((m) => m.id));

    const refresh = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/mentors');
            setMentors(data.mentors || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const generateLink = async (mentor) => {
        try {
            const { data } = await api.post(`/admin/mentors/${mentor.id}/onboarding-link`);
            setLinkModal(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to generate link');
        }
    };

    const disconnect = async (mentor) => {
        if (!window.confirm(`Disconnect ${mentor.name}'s Google Calendar? They’ll need to reconnect to receive bookings.`)) return;
        try {
            await api.delete(`/admin/mentors/${mentor.id}/connection`);
            toast.success('Disconnected.');
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };

    return (
        <AdminLayout
            title="Mentors"
            subtitle="Manage mentor card info & Google Calendar connections"
        >
            {canEdit && !loading && mentors.length > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border/60 bg-card p-3">
                    <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                        <Checkbox
                            checked={allSelected}
                            onCheckedChange={toggleSelectAll}
                            data-testid="admin-mentors-select-all"
                        />
                        Select all
                    </label>
                    <span className="text-sm text-muted-foreground" data-testid="admin-mentors-selected-count">
                        {selectedIds.length} selected
                    </span>
                    <div className="ml-auto flex gap-2">
                        <Button
                            size="sm"
                            disabled={selectedIds.length === 0}
                            onClick={() => setBulkOpen(true)}
                            data-testid="admin-mentors-bulk-edit"
                        >
                            <Users className="mr-1 h-4 w-4" /> Bulk edit
                        </Button>
                        {selectedIds.length > 0 && (
                            <Button size="sm" variant="ghost" onClick={clearSelection}>
                                <X className="mr-1 h-4 w-4" /> Clear
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {loading ? (
                <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/40 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {mentors.map((m) => {
                        const initials = m.name?.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
                        return (
                            <Card key={m.id} className={selectedIds.includes(m.id) ? 'ring-2 ring-primary' : ''}>
                                <CardContent className="p-5">
                                    <div className="flex items-start gap-3">
                                        {canEdit && (
                                            <Checkbox
                                                className="mt-1"
                                                checked={selectedIds.includes(m.id)}
                                                onCheckedChange={() => toggleSelect(m.id)}
                                                data-testid="admin-mentor-select"
                                                aria-label={`Select ${m.name}`}
                                            />
                                        )}
                                        <Avatar className="h-12 w-12">
                                            <AvatarImage src={m.avatar_url} />
                                            <AvatarFallback className="bg-secondary text-primary">{initials}</AvatarFallback>
                                        </Avatar>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center justify-between gap-2">
                                                <h3 className="truncate font-display text-base font-semibold text-primary">{m.name}</h3>
                                                {m.calendar_connected ? (
                                                    <Badge className="gap-1 bg-accent text-accent-foreground hover:bg-accent"><CheckCircle2 className="h-3 w-3" /> Connected</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="gap-1"><XCircle className="h-3 w-3" /> Not connected</Badge>
                                                )}
                                            </div>
                                            <p className="line-clamp-2 text-xs text-muted-foreground">{m.headline}</p>
                                            <div className="mt-1 flex flex-wrap items-center gap-1">
                                                {(m.domain || '').split(',').map((d) => d.trim()).filter(Boolean).map((d) => (
                                                    <span key={d} className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                                                        {d}
                                                    </span>
                                                ))}
                                                {m._has_overrides && (
                                                    <span className="inline-flex items-center gap-1 rounded-full border border-accent/30 bg-secondary px-2 py-0.5 text-[10px] text-primary">
                                                        <Pencil className="h-2.5 w-2.5" /> Edited
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                                        {m.google_account && <div><strong>Google:</strong> {m.google_account}</div>}
                                        {m.email && <div className="break-all"><strong>Email:</strong> {m.email}</div>}
                                        {m.phone && <div><strong>WhatsApp:</strong> {m.phone}</div>}
                                        <div><strong>Bookings:</strong> {m.bookings_count}</div>
                                        <div><strong>Rating:</strong> {m.rating?.toFixed?.(1) ?? 'N/A'} · <strong>Sessions:</strong> {m.sessions_completed ?? 'N/A'}</div>
                                    </div>
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {canEdit && (
                                            <>
                                                <Button size="sm" variant="secondary" onClick={() => setEditing(m)} data-testid="admin-mentors-edit">
                                                    <Pencil className="mr-1 h-4 w-4" /> Edit card
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => generateLink(m)} data-testid={ADMIN_MENTORS.onboarding_link}>
                                                    <Link2 className="mr-1 h-4 w-4" /> Onboarding link
                                                </Button>
                                                {m.calendar_connected && (
                                                    <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => disconnect(m)} data-testid={ADMIN_MENTORS.disconnect}>
                                                        <Unplug className="mr-1 h-4 w-4" /> Disconnect
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            <Dialog open={!!linkModal} onOpenChange={(o) => !o && setLinkModal(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Mentor onboarding link</DialogTitle>
                    </DialogHeader>
                    {linkModal && (
                        <div className="space-y-3 text-sm">
                            <p className="text-muted-foreground">Send this link to <strong>{linkModal.mentor_name}</strong>. When they click it, they can connect their Google Calendar.</p>
                            <div className="flex gap-2">
                                <input
                                    readOnly
                                    className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-xs"
                                    value={linkModal.onboarding_url}
                                />
                                <Button size="sm" onClick={() => { navigator.clipboard.writeText(linkModal.onboarding_url); toast.success('Copied'); }}>
                                    <Copy className="mr-1 h-4 w-4" /> Copy
                                </Button>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setLinkModal(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {editing && (
                <EditMentorDialog
                    mentor={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); refresh(); }}
                />
            )}

            {bulkOpen && (
                <BulkEditMentorsDialog
                    mentorIds={selectedIds}
                    count={selectedIds.length}
                    onClose={() => setBulkOpen(false)}
                    onSaved={() => { setBulkOpen(false); clearSelection(); refresh(); }}
                />
            )}
        </AdminLayout>
    );
}
